import client from './client';
import axios from 'axios';
import {
  Album,
  AlbumMember,
  AlbumOverlayConfig,
  AlbumPhoto,
  AlbumPreview,
  AlbumViewData,
  MyAlbumEntry,
  AdminDevToolsLog,
  Photo,
  PhotoReport,
  ReportReason,
  SlideshowConfig,
} from '../types';

export interface ZipModeStatus {
  status: 'none' | 'building' | 'ready' | 'stale';
  photoCount: number;
  pendingCount: number;
  builtAt: string | null;
  failed: boolean;
  /** When the next automatic bundle will fire, or null if none pending. */
  nextBundleAt: string | null;
  /** Photos left out of the last build because their file was corrupt. */
  skipped: Array<{ photoId: string; filename: string }>;
}

interface AlbumsListResponse {
  albums: Album[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

interface AlbumPhotosResponse {
  photos: Photo[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface MyAlbumsResponse {
  albums: MyAlbumEntry[];
}

interface MembersResponse {
  members: AlbumMember[];
}

interface DevToolsLogsResponse {
  logs: AdminDevToolsLog[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

interface ReportsResponse {
  reports: PhotoReport[];
}

export const albumsApi = {
  // ── Creator-scoped (betaApproved) ────────────────────────────────────────

  list: (eventId?: string) =>
    client
      .get<AlbumsListResponse>('/albums', { params: eventId ? { eventId } : undefined })
      .then((r) => r.data),

  create: (data: { name: string; description?: string; eventId: string; coverPhotoId?: string; isLive?: boolean; sourceFilter?: 'all' | 'selected'; sourceFtpAccountIds?: string[]; sourceIncludeManualUploads?: boolean; backfillExisting?: boolean; backfillRespectSourceFilter?: boolean; attachDefaultOverlay?: boolean; faceRecognitionEnabled?: boolean; guestVisibility?: 'all' | 'matched_only'; faceSearchScope?: 'album' | 'event'; duplicateReviewEnabled?: boolean; allowedEmailDomains?: string[] }) =>
    client.post<Album>('/albums', data).then((r) => r.data),

  get: (albumId: string) =>
    client.get<Album>(`/albums/${albumId}`).then((r) => r.data),

  update: (albumId: string, data: {
    name?: string;
    description?: string;
    overlayConfigs?: AlbumOverlayConfig[];
    coverPhotoId?: string | null;
    sourceFilter?: 'all' | 'selected';
    sourceFtpAccountIds?: string[];
    sourceIncludeManualUploads?: boolean;
    faceRecognitionEnabled?: boolean;
    guestVisibility?: 'all' | 'matched_only';
    faceSearchScope?: 'album' | 'event';
    editAccess?: 'team' | 'creator_only';
    allowedEmailDomains?: string[];
  }) =>
    client.patch<Album>(`/albums/${albumId}`, data).then((r) => r.data),

  delete: (albumId: string) =>
    client.delete(`/albums/${albumId}`).then((r) => r.data),

  regenerateToken: (albumId: string) =>
    client.post<Album>(`/albums/${albumId}/regenerate-token`).then((r) => r.data),

  listPhotos: (albumId: string, cursor?: string) =>
    client
      .get<AlbumPhotosResponse>(`/albums/${albumId}/photos`, { params: cursor ? { cursor } : undefined })
      .then((r) => r.data),

  listRejectedPhotos: (albumId: string, cursor?: string) =>
    client
      .get<AlbumPhotosResponse>(`/albums/${albumId}/rejected-photos`, { params: cursor ? { cursor } : undefined })
      .then((r) => r.data),

  restoreRejectedPhoto: (albumId: string, photoId: string) =>
    client.post<{ message: string }>(`/albums/${albumId}/rejected-photos/${photoId}/restore`).then((r) => r.data),

  bulkRestoreRejectedPhotos: (albumId: string, photoIds: string[]) =>
    client.post<{ restored: number }>(`/albums/${albumId}/rejected-photos/bulk-restore`, { photoIds }).then((r) => r.data),

  addPhotos: (albumId: string, photoIds: string[]) =>
    client.post(`/albums/${albumId}/photos`, { photoIds }).then((r) => r.data),

  removePhoto: (albumId: string, photoId: string) =>
    client.delete(`/albums/${albumId}/photos/${photoId}`).then((r) => r.data),

  listMembers: (albumId: string) =>
    client.get<MembersResponse>(`/albums/${albumId}/members`).then((r) => r.data),

  kickMember: (albumId: string, userId: string) =>
    client.delete(`/albums/${albumId}/members/${userId}`).then((r) => r.data),

  // ── Member-scoped (requireAuth) ──────────────────────────────────────────

  myAlbums: () =>
    client.get<MyAlbumsResponse>('/albums/my-albums').then((r) => r.data),

  viewAlbum: (albumId: string, cursor?: string) =>
    client
      .get<AlbumViewData>(`/albums/view/${albumId}`, { params: cursor ? { cursor } : undefined })
      .then((r) => r.data),

  /** All photo ids shown in the album — used by "Select all". */
  listPhotoIds: (albumId: string) =>
    client
      .get<{ ids: string[] }>(`/albums/view/${albumId}/ids`)
      .then((r) => r.data.ids),

  leaveAlbum: (albumId: string) =>
    client.post(`/albums/view/${albumId}/leave`).then((r) => r.data),

  // ── Public ───────────────────────────────────────────────────────────────

  getPreview: (token: string) =>
    client.get<AlbumPreview>(`/albums/join/${token}`).then((r) => r.data),

  joinAlbum: (token: string) =>
    client
      .post<{ joined?: boolean; alreadyMember?: boolean; albumId: string; faceRecognitionEnabled?: boolean }>(`/albums/join/${token}`)
      .then((r) => r.data),

  updateNotifications: (albumId: string, enabled: boolean) =>
    client
      .patch<{ notificationsEnabled: boolean }>(`/albums/view/${albumId}/notifications`, { enabled })
      .then((r) => r.data),

  // ── Face recognition ─────────────────────────────────────────────────────

  getMyPhotos: (albumId: string) =>
    client
      .get<{
        matchedPhotoIds: string[];
        reviewPhotoIds: string[];
        reviewPhotos: Array<{
          _id: string;
          thumbnailUrl: string | null;
          originalUrl: string | null;
          similarity: number;
        }>;
        pinnedPhotoIds: string[];
        highlightPhotoIds: string[];
        hasFacePhoto: boolean;
        matchCount: number;
        reviewCount: number;
        pinnedCount: number;
        highlightCount: number;
        similarity: Record<string, number>;
      }>(`/albums/${albumId}/my-photos`)
      .then((r) => r.data),

  /**
   * Confirm ("✓ yes, this is me") or reject ("✗ not me") a borderline review
   * candidate. A confirm moves the photo into My Photos and teaches the
   * user's cross-event face template; a reject hides it permanently.
   */
  confirmMyPhoto: (albumId: string, photoId: string, decision: 'confirm' | 'reject') =>
    client
      .post<{ ok: boolean; decision: 'confirm' | 'reject'; learned?: boolean; similarity?: number }>(
        `/albums/${albumId}/my-photos/confirm`,
        { photoId, decision },
      )
      .then((r) => r.data),

  // ── DevTools detection ───────────────────────────────────────────────────

  logDevTools: (albumId: string, method: 'resize' | 'keyboard') =>
    client.post('/albums/devtools-log', { albumId, method }).then((r) => r.data),

  logDownload: (albumId: string, photoIds: string[], downloadType: 'single' | 'batch' | 'all') =>
    client.post('/albums/download-log', { albumId, photoIds, downloadType }).catch(() => {}),

  /**
   * Download album photos as a ZIP.
   * For "download all" (no photoIds), the backend may return a presigned S3 URL
   * for a pre-built ZIP. For selective downloads, streams on-demand.
   *
   * Returns 'building' if the ZIP is still being generated.
   */
  downloadZip: async (albumId: string, photoIds?: string[]): Promise<'building' | void> => {
    const isSelectiveDownload = photoIds && photoIds.length > 0;

    if (!isSelectiveDownload) {
      // Try JSON response first (pre-built ZIP redirect)
      const jsonResponse = await client.post(
        `/albums/${albumId}/download`,
        {},
        { responseType: 'json' },
      );

      const data = jsonResponse.data as { redirectUrl?: string; filename?: string; building?: boolean; isStale?: boolean };

      if (data.building) {
        return 'building';
      }

      if (data.redirectUrl) {
        // Download via presigned S3 URL
        const a = document.createElement('a');
        a.href = data.redirectUrl;
        a.download = data.filename || 'album.zip';
        a.click();
        return;
      }
    }

    // Selective download or fallback: stream on-demand ZIP
    const response = await client.post(
      `/albums/${albumId}/download`,
      photoIds ? { photoIds } : {},
      { responseType: 'blob' },
    );

    // Extract filename from Content-Disposition header if available
    const disposition = response.headers['content-disposition'] as string | undefined;
    let filename = 'album.zip';
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    // Trigger browser download from the blob
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Download the pre-built HIGHLIGHTS ZIP (highlights + pins) for a restricted
   * (matched_only face-rec) viewer. Returns 'building' when no pre-built ZIP is
   * ready yet, so the caller can fall back to a client-side download.
   */
  downloadHighlightsZip: async (albumId: string): Promise<'building' | void> => {
    const { data } = await client.post(
      `/albums/${albumId}/download-highlights`,
      {},
      { responseType: 'json' },
    );
    const res = data as { redirectUrl?: string; filename?: string; building?: boolean };
    if (res.building) return 'building';
    if (res.redirectUrl) {
      const a = document.createElement('a');
      a.href = res.redirectUrl;
      a.download = res.filename || 'highlights.zip';
      a.click();
      return;
    }
    return 'building';
  },

  /** ZIP build status (whole-album + highlights) for the creator's album view. */
  getAlbumZipStatus: (albumId: string) =>
    client
      .get<{
        all: ZipModeStatus;
        highlights: ZipModeStatus & { applicable: boolean };
      }>(`/albums/${albumId}/zip-status`)
      .then((r) => r.data),

  // ── Live album ───────────────────────────────────────────────────────────

  toggleLike: (albumId: string, photoId: string) =>
    client.post<{ liked: boolean; likeCount: number }>(`/albums/${albumId}/photos/${photoId}/like`).then((r) => r.data),

  togglePin: (albumId: string, photoId: string) =>
    client.patch<AlbumPhoto>(`/albums/${albumId}/photos/${photoId}/pin`).then((r) => r.data),

  getProcessedUrl: (albumId: string, photoId: string) =>
    client.get<{ processedStatus: AlbumPhoto['processedStatus']; fullUrl: string | null; thumbnailUrl: string | null }>(`/albums/${albumId}/photos/${photoId}/processed`).then((r) => r.data),

  getLiveStatus: (albumId: string) =>
    client.get<{
      hasOverlays: boolean;
      processedCount: number;
      pendingCount: number;
      failedCount: number;
      lastPhotoAddedAt: string | null;
      lastProcessedAt: string | null;
    }>(`/albums/${albumId}/live-status`).then((r) => r.data),

  getFaceIndexStatus: (albumId: string) =>
    client.get<{
      enabled: boolean;
      status: 'idle' | 'running' | 'completed' | 'failed';
      processed: number;
      failed: number;
      total: number;
      error: string | null;
      attempts: number;
      startedAt: string | null;
      completedAt: string | null;
      hasCollection: boolean;
    }>(`/albums/${albumId}/face-index-status`).then((r) => r.data),

  retryFaceIndex: (albumId: string) =>
    client.post<{ status: 'running' }>(`/albums/${albumId}/face-index/retry`).then((r) => r.data),

  getProcessingStatus: (albumId: string) =>
    client.get<{
      processingStatus: 'idle' | 'processing' | 'ready' | 'failed';
      hasOverlays: boolean;
      processedCount: number;
      pendingCount: number;
      failedCount: number;
      total: number;
    }>(`/albums/${albumId}/processing-status`).then((r) => r.data),

  retryProcessing: (albumId: string) =>
    client.post<{ status: 'processing' | 'ready'; total: number; alreadyRunning?: boolean }>(`/albums/${albumId}/processing/retry`).then((r) => r.data),

  // ── Photo Reports ────────────────────────────────────────────────────────

  reportPhoto: (albumId: string, photoId: string, data: { reason: ReportReason; message?: string }) =>
    client.post<PhotoReport>(`/albums/${albumId}/photos/${photoId}/report`, data).then((r) => r.data),

  listReports: (albumId: string, status?: string) =>
    client.get<ReportsResponse>(`/albums/${albumId}/reports`, { params: status ? { status } : undefined }).then((r) => r.data),

  resolveReport: (albumId: string, reportId: string, data: { action: 'dismiss' | 'action' }) =>
    client.patch<{ message: string }>(`/albums/${albumId}/reports/${reportId}`, data).then((r) => r.data),

  // ── Slideshow ────────────────────────────────────────────────────────────

  enableSlideshow: (albumId: string, password: string) =>
    client.post<{ slideshowToken: string; slideshowCode: string; slideshowUrl: string }>(
      `/albums/${albumId}/slideshow/enable`, { password },
    ).then((r) => r.data),

  disableSlideshow: (albumId: string) =>
    client.post(`/albums/${albumId}/slideshow/disable`).then((r) => r.data),

  slideshowAuth: (
    albumId: string,
    password: string,
    credential: { slideshowToken?: string; code?: string },
  ) =>
    // Use a fresh axios instance with withCredentials so the cookie gets set
    axios.post<{ message: string; albumName: string; slideshowConfig: SlideshowConfig }>(
      `/api/v1/albums/${albumId}/slideshow/auth`,
      { password, ...credential },
      { withCredentials: true },
    ).then((r) => r.data),

  /** Public: turns a typed display code into the album it belongs to. */
  resolveSlideshowCode: (code: string) =>
    axios.get<{ albumId: string; albumName: string }>(
      `/api/v1/albums/slideshow/code/${encodeURIComponent(code)}`,
    ).then((r) => r.data),

  getSlideshowPhotos: (albumId: string, cursor?: string) =>
    axios.get<{
      photos: Photo[];
      photoMeta: Record<string, { likeCount: number; isPinned: boolean }>;
      nextCursor: string | null;
      hasMore: boolean;
      /** Photos in the frozen snapshot this cursor walks. */
      snapshotCount: number;
      totalCount: number;
      albumName: string;
      eventName: string;
      slideshowConfig: SlideshowConfig;
    }>(`/api/v1/albums/${albumId}/slideshow/photos`, {
      params: cursor ? { cursor } : undefined,
      withCredentials: true,
    }).then((r) => r.data),

  /**
   * Cheap liveness probe — returns the album's photo total without loading or
   * signing any photo URLs. The viewer polls this to detect new arrivals.
   */
  getSlideshowCount: (albumId: string) =>
    axios.get<{
      totalCount: number;
      albumName: string;
      eventName: string;
      slideshowConfig: SlideshowConfig;
    }>(`/api/v1/albums/${albumId}/slideshow/photos`, {
      params: { countOnly: '1' },
      withCredentials: true,
    }).then((r) => r.data),

  /** Creator-only: the short display code and the path built from it. */
  getSlideshowLink: (albumId: string) =>
    client.get<{ code: string; path: string }>(`/albums/${albumId}/slideshow/link`).then((r) => r.data),

  /** Creator-only: reveals the current slideshow password in plaintext. */
  getSlideshowPassword: (albumId: string) =>
    client.get<{ password: string | null }>(`/albums/${albumId}/slideshow/password`).then((r) => r.data),

  updateSlideshowConfig: (albumId: string, config: Partial<SlideshowConfig>) =>
    client.patch<{ slideshowConfig: SlideshowConfig }>(`/albums/${albumId}/slideshow/config`, config).then((r) => r.data),

  resetSlideshowPassword: (albumId: string, password: string) =>
    client.post(`/albums/${albumId}/slideshow/reset-password`, { password }).then((r) => r.data),

  // ── Analytics ────────────────────────────────────────────────────────────

  getAnalytics: (albumId: string) =>
    client.get<{
      totalPhotos: number;
      totalMembers: number;
      totalLikes: number;
      totalDownloads: number;
      topPhotos: Array<{ photoId: string; thumbnailUrl: string; likeCount: number; filename: string }>;
      recentActivity: Array<{ type: 'like' | 'join' | 'download'; userId: string; userName: string; timestamp: string }>;
      photosByDay: Array<{ date: string; count: number }>;
    }>(`/albums/${albumId}/analytics`).then((r) => r.data),

  getPhotoLikes: (albumId: string, photoId: string) =>
    client.get<{
      likes: Array<{ userId: string; name: string; likedAt: string }>;
      totalCount: number;
      publicCount: number;
    }>(`/albums/${albumId}/photos/${photoId}/likes`).then((r) => r.data),

  triggerZip: (albumId: string) =>
    client.post<{ status: 'triggered' | 'building' }>(`/albums/${albumId}/trigger-zip`).then((r) => r.data),

  // ── Admin ────────────────────────────────────────────────────────────────

  listDevToolsLogs: (page = 1) =>
    client.get<DevToolsLogsResponse>('/admin/devtools-logs', { params: { page } }).then((r) => r.data),
};
